import {
  SchoolEntity,
  SchoolEntityInput,
} from "../../../services/schools/school.types";
import { SchoolDiaryType } from "../../../types/enums";

export const initialValuesSchool: SchoolEntityInput = {
  cct: "",
  city: "",
  demo: true,
  // New schools default to the full-day diary (matches the DB default).
  diary_type: SchoolDiaryType.FULL_DAY,
  name: "",
  abbreviation: "",
  programs: [],
  groups: [],
};

/**
 * The school-level scalars every save carries, in one place.
 *
 * They travel in TWO separate PATCHes: the "Guardar / Actualizar" submit and
 * the Programas multiselect commit, which rebuilds the whole school payload to
 * reach the backend's program diff. A flag added to only one of them is wiped
 * in silence by the other, so both callers build their payload from here.
 *
 * `programs` and `groups` are deliberately absent, not empty: the backend only
 * diffs (and cascade soft-deletes) an array that is present in the payload.
 * Callers that do mean to manage programs add the key themselves.
 *
 * A flag the backend never returned stays `undefined` and is therefore dropped
 * from the JSON body, so an older backend keeps its own default instead of
 * receiving a `false` nobody chose.
 *
 * SCRUM-916: `isCampus` deja fuera la configuración COMPARTIDA (bitácora,
 * demo, manipulativos), que es de la escuela principal. No es cosmético: un
 * PATCH de plantel que traiga cualquiera de esas claves — aunque sea con el
 * mismo valor heredado que devolvió el GET — responde 400
 * `CAMPUS_CONFIG_READONLY` y bloquea hasta un cambio de nombre.
 */
export const buildSchoolScalarPayload = (
  values: SchoolEntityInput,
  isCampus = false
): SchoolEntityInput => ({
  cct: values.cct,
  city: values.city,
  name: values.name,
  abbreviation: values.abbreviation,
  ...(isCampus
    ? {}
    : {
        demo: values.demo,
        diary_type: values.diary_type,
        // SCRUM-907
        primaria_diary_enabled: values.primaria_diary_enabled,
        // SCRUM-795 / SCRUM-922
        kinder_math_without_manipulatives:
          values.kinder_math_without_manipulatives,
        kinder_literacy_without_manipulatives:
          values.kinder_literacy_without_manipulatives,
        primaria_math_without_manipulatives:
          values.primaria_math_without_manipulatives,
        primaria_literacy_without_manipulatives:
          values.primaria_literacy_without_manipulatives,
      }),
});

/**
 * SCRUM-1034: la abreviatura es única entre TODAS las escuelas vivas
 * (principales y planteles), sin distinguir mayúsculas ni espacios al borde.
 * El backend rechaza el duplicado con un 409 que trae este mismo aviso;
 * validarlo también acá lo muestra en el campo antes de enviar. El texto es el
 * que pidió producto: no cambiarlo sin cambiarlo también en school.utils.ts.
 */
export const ABBREVIATION_TAKEN_MESSAGE =
  "Esta abreviatura ya está asignada a otra escuela. Ingresa una abreviatura diferente.";

export const normalizeAbbreviation = (
  value: string | null | undefined
): string => (value ?? "").trim().toLowerCase();

export type AbbreviationOwner = Pick<
  SchoolEntity,
  "id" | "name" | "abbreviation"
>;

/**
 * Devuelve la escuela (distinta de `excludeSchoolId`) que ya usa la
 * abreviatura, o null si está libre o viene vacía.
 */
export const findAbbreviationOwner = (
  abbreviation: string | null | undefined,
  schools: AbbreviationOwner[],
  excludeSchoolId?: number
): AbbreviationOwner | null => {
  const wanted = normalizeAbbreviation(abbreviation);
  if (!wanted) return null;
  return (
    schools.find(
      (school) =>
        school.id !== excludeSchoolId &&
        normalizeAbbreviation(school.abbreviation) === wanted
    ) ?? null
  );
};
